import Vue from 'vue'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { pedidoService } from '@/services'
import tareasService from '@/services/tareas.service'

const PASO_UI_POR_TIPO = {
  ENTREGARPEDIDO: 'entrega',
  ENTREGA: 'entrega',
  FACTURARPEDIDO: 'factura',
  FACTURA: 'factura',
  TRANSFERIRSTOCK: 'transferencia',
  TRANSFERENCIASTOCK: 'transferencia',
  TRANSFERENCIA: 'transferencia',
  FINALIZARDESPACHO: 'finalizado',
  FINALIZADO: 'finalizado'
}

const PASOS_UI_ORDEN = ['entrega', 'factura', 'transferencia', 'finalizado']

function normalizarPasoUi(tipo) {
  if (!tipo) return null
  const normalizado = String(tipo).trim().toUpperCase()
  return PASO_UI_POR_TIPO[normalizado] ?? null
}

function normalizarEstadoPaso(estado) {
  const valor = String(estado ?? '')
    .trim()
    .toLowerCase()

  if (!valor) return 'pendiente'
  if (
    [
      'ok',
      'completada',
      'completado',
      'finalizado',
      'exitoso',
      'success'
    ].includes(valor)
  ) {
    return 'ok'
  }
  if (
    [
      'error',
      'fallida',
      'fallido',
      'failed',
      'faulted',
      'cancelada',
      'cancelado'
    ].includes(valor)
  ) {
    return 'error'
  }
  if (
    ['loading', 'enproceso', 'procesando', 'running', 'inprogress'].includes(
      valor
    )
  ) {
    return 'loading'
  }
  return 'pendiente'
}

function obtenerPasosFlujo(pasos = []) {
  if (!Array.isArray(pasos) || pasos.length === 0) {
    return [...PASOS_UI_ORDEN]
  }

  const vistos = {}
  const ordenados = []

  pasos.forEach((paso) => {
    const ui = normalizarPasoUi(paso)
    if (!ui || vistos[ui]) return
    vistos[ui] = true
    ordenados.push(ui)
  })

  if (!vistos.finalizado) {
    ordenados.push('finalizado')
  }

  return ordenados
}

function deducirPasoError(estadoPasos, pasosFlujo) {
  const orden =
    Array.isArray(pasosFlujo) && pasosFlujo.length > 0
      ? pasosFlujo
      : PASOS_UI_ORDEN
  for (const paso of orden) {
    if (estadoPasos[paso] === 'error') return paso
  }
  return null
}

function normalizarDoc(doc, tipoContenedor) {
  if (doc === null || doc === undefined) return null

  if (typeof doc === 'number' || typeof doc === 'string') {
    return {
      tipo: tipoContenedor,
      docEntry: null,
      docNum: doc,
      comments: null,
      vatSum: null,
      docTotal: null
    }
  }

  const tipoDoc = doc.tipo ?? doc.Tipo ?? tipoContenedor

  return {
    tipo: tipoDoc,
    docEntry: doc.docEntry ?? doc.DocEntry ?? null,
    docNum:
      doc.docNum ?? doc.DocNum ?? doc.numero ?? doc.numeroDocumento ?? null,
    comments: doc.comments ?? doc.Comments ?? null,
    vatSum: doc.vatSum ?? doc.VatSum ?? null,
    docTotal: doc.docTotal ?? doc.DocTotal ?? null
  }
}

function construirUrlHubTareas(apiUrl) {
  const baseUrlNormalizada = String(apiUrl ?? '')
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/api$/i, '')

  return `${baseUrlNormalizada}/hubs/tareas`
}

function debugNotificacionAsyncEntrega(identificador, payload) {
  if (process.env.NODE_ENV === 'production') return

  const evento = {
    identificador,
    timestamp: new Date().toISOString()
  }

  if (payload !== undefined) {
    evento.payload = payload
  }

  // eslint-disable-next-line no-console
  console.debug('[despacho][async][ws] Notificacion recibida', evento)
}

function normalizarDocsPorPaso(data) {
  const docsPorPaso = {
    entrega: [],
    factura: [],
    transferencia: [],
    finalizado: []
  }

  const fuentes = [
    { paso: 'entrega', docs: data.entregas ?? data.Entregas ?? [] },
    { paso: 'factura', docs: data.facturas ?? data.Facturas ?? [] },
    {
      paso: 'transferencia',
      docs: data.transferencias ?? data.Transferencias ?? []
    }
  ]

  fuentes.forEach(({ paso, docs }) => {
    docs.forEach((doc) => {
      const item = normalizarDoc(doc, paso)
      if (!item) return
      docsPorPaso[paso].push(item)
    })
  })

  return docsPorPaso
}

function normalizarEstadoAsyncStatus(data, pasosFlujo) {
  const estadoPasos = {
    entrega: 'pendiente',
    factura: 'pendiente',
    transferencia: 'pendiente',
    finalizado: 'pendiente'
  }

  const estadoPorTipo = data.estadoPorTipo ?? data.EstadoPorTipo ?? {}
  Object.entries(estadoPorTipo).forEach(([tipo, estado]) => {
    const pasoUi = normalizarPasoUi(tipo)
    if (!pasoUi) return
    estadoPasos[pasoUi] = normalizarEstadoPaso(estado)
  })

  const docsPorPaso = normalizarDocsPorPaso(data)
  if (docsPorPaso.entrega.length > 0 && estadoPasos.entrega === 'pendiente') {
    estadoPasos.entrega = 'ok'
  }
  if (docsPorPaso.factura.length > 0 && estadoPasos.factura === 'pendiente') {
    estadoPasos.factura = 'ok'
  }
  if (
    docsPorPaso.transferencia.length > 0 &&
    estadoPasos.transferencia === 'pendiente'
  ) {
    estadoPasos.transferencia = 'ok'
  }

  const errorPayload = data.error ?? data.Error ?? {}
  const mensajeError =
    errorPayload.mensajeError ??
    errorPayload.MensajeError ??
    data.mensajeError ??
    data.MensajeError ??
    null

  const errorPasoInformado =
    normalizarPasoUi(errorPayload.errorPaso) ??
    normalizarPasoUi(errorPayload.ErrorPaso) ??
    null

  const errorPaso =
    errorPasoInformado ?? deducirPasoError(estadoPasos, pasosFlujo)

  const errorDocNum =
    errorPayload.errorDocNum ?? errorPayload.ErrorDocNum ?? null

  const finalizado =
    Boolean(data.finalizado ?? data.Finalizado) ||
    estadoPasos.finalizado === 'ok'

  if (finalizado) {
    estadoPasos.finalizado = 'ok'
  }

  return {
    estadoPasos,
    docsPorPaso,
    error: {
      mensajeError,
      errorPaso,
      errorDocNum
    },
    finalizado
  }
}

export default {
  namespaced: true,
  state: {
    ids: [],
    pedidos: [],
    entregas: [],
    estado: '',
    mostrarConfirmacion: false,
    tamanoPagina: 30,
    _ultimoClick: null,
    identificador: null,
    pasos: [],
    estadoPasos: {},
    docsPorPaso: {},
    mensajeError: null,
    error: null,
    errorAsync: null,
    hubConnection: null,
    finalizadoAsync: false
  },
  getters: {
    pedidos: (state) => state.pedidos,
    cargando: (state) => state.estado === 'loading',
    pedidoPadre: (state) => (state.pedidos.length > 0 ? state.pedidos[0] : {}),
    subpedidos: (state) =>
      state.pedidos.length === 1
        ? state.pedidos
        : state.pedidos.filter((item, index) => index > 0),
    pedidoExiste: (state) => state.ids.length > 0,
    hayError: (state) => state.estado === 'error',
    puedeEntregarPedido: (state, getters) => {
      // Si no hay pedidos, no se puede entregar
      if (state.pedidos.length === 0) return false
      const pedido = state.pedidos[0]
      // Si pedido está despachado o cancelado, no se puede entregar
      const estados = ['despachado', 'cerrado', 'cancelado']
      if (estados.includes(pedido.estado.toLowerCase())) return false
      // Si no hay ítems, no se puede entregar
      if (!pedido.items) return false
      // Si debe confirmar entrega parcial, no se puede entregar
      if (getters.debeConfirmarEntregaParcial) return false
      return pedido.items.some((item) => {
        return !item.fueEntregado
      })
    },
    debeConfirmarEntregaParcial: (state, getters) => {
      if (state.pedidos.length === 0) return false
      let items = []
      if (state.pedidos.length === 1) items = state.pedidos[0].items
      else getters.subpedidos.forEach((pedido) => items.push(...pedido.items))
      return items.some((item) => {
        const entregaIncompleta =
          item.cantidadEntregada < item.cantidadSolicitada
        const noConfirmado = !item.confirmaEntrega
        if (item.esKit) {
          // Item kit sin confirmar entrega parcial
          if (entregaIncompleta && noConfirmado) {
            return true
          }
        } else {
          // Item normal con stock disponible sin confirmar
          if (
            entregaIncompleta &&
            noConfirmado &&
            item.stock >= item.cantidadSolicitada &&
            !item.pedidoItemKitId
          ) {
            return true
          }
        }
        return false
      })
    },
    mostrarConfirmacion: (state) => state.mostrarConfirmacion,
    entregas: (state) => state.entregas,
    hayMedidaInventario: (state) => {
      if (state.pedidos.length === 0) return false
      return state.pedidos.some((x) =>
        x.items.some(
          (y) => y.unidadMedida !== (y.unidadMedidaInventario ?? y.unidadMedida)
        )
      )
    }
  },
  mutations: {
    PEDIDOS_REQUEST: (state) => {
      if (state.estado !== 'loading') state.estado = 'loading'
    },
    PEDIDO_SUCCESS: (state, payload) => {
      state.pedidos.push(payload)
      state.pedidos = state.pedidos.sort(
        (a, b) => parseInt(a.id) - parseInt(b.id)
      )
    },
    PEDIDOS_SUCCESS: (state) => {
      state.estado = 'ok'
    },
    PEDIDOS_ERROR: (state) => {
      state.estado = 'error'
      state.pedidos = []
    },
    SEARCH_REQUEST: (state) => {
      state.ids = []
      state.pedidos = []
      state.entregas = []
      state.estado = 'loading'
      state.mostrarConfirmacion = false
      state.identificador = null
      state.pasos = []
      state.estadoPasos = {}
      state.docsPorPaso = {}
      state.mensajeError = null
      state.error = null
      state.errorAsync = null
      state.finalizadoAsync = false
    },
    SEARCH_SUCCESS: (state, payload) => {
      state.ids = payload
    },
    SEARCH_ERROR: (state) => {
      state.estado = 'error'
      state.ids = []
    },
    UPDATE_PEDIDO: (state, payload) => {
      let found = false
      for (const [indexPedido, pedido] of state.pedidos.entries()) {
        for (const [indexItem, item] of pedido.items.entries()) {
          if (item.id === payload.id) {
            state.pedidos[indexPedido].items[indexItem].cantidadEntregada =
              payload.cantidadEntregada
            state.pedidos[indexPedido].items[indexItem].confirmaEntrega =
              payload.cantidadEntregada ===
              state.pedidos[indexPedido].items[indexItem].cantidadSolicitada
            found = true
            break
          }
        }
        if (found) break
      }
    },
    UPDATE_ENTREGA: (state, payload) => {
      let found = false
      for (const [indexPedido, pedido] of state.pedidos.entries()) {
        for (const [indexItem, item] of pedido.items.entries()) {
          if (item.id === payload.id) {
            state.pedidos[indexPedido].items[indexItem].confirmaEntrega = true
            found = true
            break
          }
        }
        if (found) break
      }
    },
    DELIVER_PEDIDO_INIT: (state) => {
      state.estado = 'loading'
      state.mensajeError = null
      state.error = null
      state.errorAsync = null
      state.finalizadoAsync = false
      state.estadoPasos = {}
      state.docsPorPaso = {}
    },
    DELIVER_PEDIDO_SUCCESS: (state, payload) => {
      if (state.estado !== 'ok') state.estado = 'ok'
      state.entregas = payload
      state.mostrarConfirmacion = true
    },
    DELIVER_PEDIDO_ERROR: (state) => {
      if (state.estado !== 'error') state.estado = 'error'
    },
    DELIVER_ASYNC_SUCCESS: (state, payload) => {
      state.estado = 'ok'
      state.identificador = payload.identificador
      state.pasos = payload.pasos
      state.mostrarConfirmacion = true
    },
    SET_HUB_CONNECTION: (state, conn) => {
      state.hubConnection = conn
    },
    SET_ESTADO_PASO: (state, { tipo, estado }) => {
      const pasoUi = normalizarPasoUi(tipo) ?? tipo
      Vue.set(state.estadoPasos, pasoUi, normalizarEstadoPaso(estado))
    },
    SET_DOC_PASO: (state, { tipo, docNum, docEntry }) => {
      const pasoUi = normalizarPasoUi(tipo) ?? tipo
      const actuales = Array.isArray(state.docsPorPaso[pasoUi])
        ? state.docsPorPaso[pasoUi]
        : []

      const existe = actuales.some(
        (doc) => doc.docNum === docNum && doc.docEntry === docEntry
      )
      if (existe) return

      Vue.set(state.docsPorPaso, pasoUi, [
        ...actuales,
        {
          tipo: pasoUi,
          docNum,
          docEntry,
          comments: null,
          vatSum: null,
          docTotal: null
        }
      ])
    },
    SET_MENSAJE_ERROR: (state, msg) => {
      state.mensajeError = msg
    },
    SET_ERROR_ASYNC: (state, payload) => {
      state.error = payload
      state.errorAsync = payload
      state.mensajeError = payload?.mensajeError ?? null
    },
    APPLY_ASYNC_STATUS: (state, payload) => {
      state.estadoPasos = payload.estadoPasos
      state.docsPorPaso = payload.docsPorPaso
      state.error = payload.error
      state.errorAsync = payload.error
      state.mensajeError = payload.error?.mensajeError ?? null
      state.finalizadoAsync = payload.finalizado
    },
    SET_FINALIZADO_ASYNC: (state, val) => {
      state.finalizadoAsync = val
    },
    RESET_ASYNC_PROGRESS: (state) => {
      state.identificador = null
      state.pasos = []
      state.estadoPasos = {}
      state.docsPorPaso = {}
      state.errorAsync = null
      state.error = null
      state.mensajeError = null
      state.finalizadoAsync = false
    }
  },
  actions: {
    async getPedidos({ state, commit, dispatch }) {
      commit('PEDIDOS_REQUEST')
      await Promise.all(state.ids.map((id) => dispatch('getPedido', id))).then(
        (_) => commit('PEDIDOS_SUCCESS')
      )
    },
    async getPedido({ commit }, payload) {
      await pedidoService
        .getPedido(payload)
        .then((response) => commit('PEDIDO_SUCCESS', response.data))
        .catch((_) => commit('PEDIDOS_ERROR'))
    },
    async setPedido({ commit, dispatch }, payload) {
      commit('SEARCH_REQUEST')
      await pedidoService
        .updateStockPedido(payload.numeroDocumento)
        .catch((_) => commit('SEARCH_ERROR'))
      await pedidoService
        .getPedidoByParams(payload)
        .then((response) => {
          commit(
            'SEARCH_SUCCESS',
            response.data.map((item) => item.id)
          )
        })
        .catch((_) => commit('SEARCH_ERROR'))
      await dispatch('getPedidos')
    },
    async setCantidadDespachada({ state, dispatch, commit }, payload) {
      const updateEvent = () => {
        dispatch('updatePedidoItem', payload).then(() =>
          commit('UPDATE_PEDIDO', payload)
        )
        state._ultimoClick = null
      }
      clearTimeout(state._ultimoClick)
      state._ultimoClick = setTimeout(updateEvent, 500)
    },
    async confirmarEntregaParcial({ commit }, payload) {
      await pedidoService
        .confirmarEntregaParcial(payload.id)
        .then(() => commit('UPDATE_ENTREGA', payload))
    },
    async updatePedidoItem({ commit }, payload) {
      await pedidoService
        .updatePedidoItem(payload)
        .then(() => commit('UPDATE_PEDIDO', payload))
    },
    async entregarPedido({ commit, rootState, dispatch }, payload) {
      commit('DELIVER_PEDIDO_INIT', payload)
      const facturarDesdeMopi =
        rootState.sesion.bodega?.facturarDesdeMopi ?? false

      if (!facturarDesdeMopi) {
        try {
          const rs = await pedidoService.entregarPedido(payload)
          commit('DELIVER_PEDIDO_SUCCESS', rs.data)
        } catch (error) {
          commit('DELIVER_PEDIDO_ERROR')
          throw error.response?.data?.error ?? error
        }
        return
      }

      try {
        const rs = await pedidoService.despacharAsync(payload)
        commit('DELIVER_ASYNC_SUCCESS', rs.data)
        const { identificador, pasos } = rs.data
        localStorage.setItem(
          `entrega_${payload}`,
          JSON.stringify({ identificador, pasos })
        )
        dispatch('_conectarHub', { identificador, pedidoId: payload })
        dispatch('consultarEstado', { identificador, pedidoId: payload })
      } catch (error) {
        const status = error.response?.status
        const code = error.response?.data?.code

        if (status === 409 && code === 'BODEGA_FLOW_MISMATCH') {
          // Refrescamos el token para que el estado de sesión/bodega quede
          // sincronizado en el próximo intento. getSesion requiere nombreUsuario
          // y bodega externos, por lo que no es invocable aquí de forma segura;
          // refreshToken es autónomo y suficiente para renovar credenciales.
          dispatch('sesion/refreshToken', null, { root: true }).catch(() => {})
          commit('DELIVER_PEDIDO_ERROR')
          throw new Error(
            `Esta bodega no tiene habilitado el flujo async. Contactá a soporte (BODEGA_FLOW_MISMATCH).`
          )
        }

        commit('DELIVER_PEDIDO_ERROR')
        throw error.response?.data?.error ?? error
      }
    },
    async _conectarHub(
      { commit, state, rootState, dispatch },
      { identificador, pedidoId }
    ) {
      const hubUrl = construirUrlHubTareas(process.env.VUE_APP_API_URL)
      const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => rootState.sesion.token
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build()

      const notificacionRecibida = (payload) => {
        debugNotificacionAsyncEntrega(identificador, payload)

        // El evento SignalR se trata como invalidación de caché.
        // La fuente de verdad del estado es GET /status.
        if (!state.identificador) return
        dispatch('consultarEstado', {
          identificador,
          pedidoId
        })
      }

      connection.on('UpdateStatus', notificacionRecibida)
      connection.on(`job-${identificador}`, notificacionRecibida)

      try {
        await connection.start()
        await connection.invoke('conectar-status-tareas', identificador)
      } catch (err) {
        commit(
          'SET_MENSAJE_ERROR',
          'Error al conectar al servidor en tiempo real. Verificar conexión.'
        )
        commit('SET_HUB_CONNECTION', null)
        return
      }
      commit('SET_HUB_CONNECTION', connection)
    },
    async consultarEstado({ commit, state }, { identificador, pedidoId }) {
      try {
        const response = await tareasService.getEstado(identificador)
        const data = response.data

        const pasosFlujo = obtenerPasosFlujo(state.pasos)
        const normalizado = normalizarEstadoAsyncStatus(data, pasosFlujo)
        commit('APPLY_ASYNC_STATUS', normalizado)

        if (normalizado.finalizado) {
          localStorage.removeItem(`entrega_${pedidoId}`)
        }
      } catch (_) {
        // consulta de estado es best-effort
      }
    },
    cerrarHubConnection({ commit, state }) {
      if (state.hubConnection) {
        state.hubConnection.stop()
        commit('SET_HUB_CONNECTION', null)
      }
    },
    async reintentarDespacho({ commit, state, dispatch }, pedidoId) {
      try {
        commit('DELIVER_PEDIDO_INIT')
        commit('RESET_ASYNC_PROGRESS')
        await pedidoService.reintentarDespacho(pedidoId)
        await dispatch('_conectarHub', {
          identificador: state.identificador,
          pedidoId
        })
        await dispatch('consultarEstado', {
          identificador: state.identificador,
          pedidoId
        })
      } catch (error) {
        commit(
          'SET_MENSAJE_ERROR',
          error.response?.data?.error ?? error.message ?? 'Error al reintentar'
        )
      }
    }
  }
}
